import sortBy from 'lodash-es/sortBy';
import { type ForwardedRef, forwardRef, useEffect, useMemo } from 'react';
import {
  type ListBusinessUnitsQuery,
  type ListBusinessUnitsQueryVariables,
  useListBusinessUnitsQuery,
} from '@/common/graphql';
import type { OrgHierarchyRelationItem } from '@/common/utils';
import { isDisabledByRelation } from '@/common/utils';
import type { ComboboxItemWithFormatted, CommonProps, UseQueryReturn } from '../props';
import { SelectMultiple, type SelectMultipleProps } from '../SelectMultiple';
import { SelectSingle, type SelectSingleProps } from '../SelectSingle';

export type BusinessUnitSelectVariables = Partial<
  Omit<ListBusinessUnitsQueryVariables, 'pageNumber' | 'pageSize' | 'keyword'>
>;
type Data = ComboboxItemWithFormatted &
  Omit<ListBusinessUnitsQuery['listBusinessUnits']['items'][number], 'id' | 'name' | 'enabled'>;
type Props = CommonProps<BusinessUnitSelectVariables> & { orgRelations?: OrgHierarchyRelationItem[] };

const useQuery = (
  variables: BusinessUnitSelectVariables | undefined,
  orgRelations: OrgHierarchyRelationItem[] | undefined,
  onLoadingCompleted?: () => void,
): UseQueryReturn<ListBusinessUnitsQueryVariables, Data> => {
  const { data, ...rest } = useListBusinessUnitsQuery({
    variables: {
      pageNumber: null,
      pageSize: null,
      keyword: null,
      enabled: variables?.enabled ?? null,
      hasEmployees: variables?.hasEmployees,
      excludeIds: variables?.excludeIds ?? null,
      companyId: variables?.companyId ?? null,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (onLoadingCompleted) onLoadingCompleted();
  }, [onLoadingCompleted]);

  const unsorted = useMemo(
    () =>
      data?.listBusinessUnits?.items?.map(it => ({
        ...it,
        value: it.id,
        label: it.name,
        disabled: !it.enabled || isDisabledByRelation(it.id, orgRelations ?? []),
      })) || [],
    [data, orgRelations],
  );

  return {
    data: sortBy(unsorted, it => it.disabled),
    ...rest,
  };
};

export const BusinessUnitSelectSingle = forwardRef(
  (props: Props & SelectSingleProps<Data>, ref: ForwardedRef<HTMLDivElement>) => {
    const { data, loading } = useQuery(props.variables, props.orgRelations, props.onLoadingCompleted);
    return <SelectSingle ref={ref} {...props} data={data} loading={loading} />;
  },
);

export const BusinessUnitSelectMultiple = (props: Props & SelectMultipleProps<Data>) => {
  const { data, loading } = useQuery(props.variables, props.orgRelations, props.onLoadingCompleted);
  return (
    <SelectMultiple
      multipleSelectedLabel={length => `${length} business units`}
      {...props}
      data={data}
      loading={loading}
    />
  );
};
